import React, { useState } from "react";
import { BlueprintSection, NewsletterSource } from "../../types/contentTools";

interface SourcesPanelProps {
  sources: NewsletterSource[];
  sections: BlueprintSection[];
  onAdd: (payload: {
    url: string;
    title?: string;
    instructions?: string;
    preferred_section_indexes?: number[];
  }) => Promise<void>;
  onUpdate: (
    sourceId: string,
    payload: {
      url?: string;
      title?: string;
      instructions?: string;
      preferred_section_indexes?: number[];
    },
  ) => Promise<void>;
  onDelete: (sourceId: string) => Promise<void>;
}

/**
 * Source mapping, mirroring the admin Sources page for a single blueprint:
 * list every source in the org, edit its URL/title/instructions, choose which
 * section(s) it prefers (none = any section), add new sources, and delete.
 */
const SourcesPanel: React.FC<SourcesPanelProps> = ({
  sources,
  sections,
  onAdd,
  onUpdate,
  onDelete,
}) => {
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [notice, setNotice] = useState<{
    key: string;
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Add-source form state
  const [newUrl, setNewUrl] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [newInstructions, setNewInstructions] = useState("");
  const [newPreferred, setNewPreferred] = useState<number[]>([]);
  const [adding, setAdding] = useState(false);

  const run = (key: string, fn: () => Promise<void>) => {
    setPending((prev) => new Set(prev).add(key));
    setNotice(null);
    fn()
      .then(() => setNotice({ key, type: "success", text: "Saved." }))
      .catch((e: any) =>
        setNotice({ key, type: "error", text: e?.message || "Failed to save." }),
      )
      .finally(() =>
        setPending((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        }),
      );
  };

  const handleAdd = async () => {
    if (!newUrl.trim()) {
      setNotice({ key: "add", type: "error", text: "URL is required." });
      return;
    }
    setAdding(true);
    setNotice(null);
    try {
      await onAdd({
        url: newUrl.trim(),
        title: newTitle.trim() || undefined,
        instructions: newInstructions.trim() || undefined,
        preferred_section_indexes: newPreferred,
      });
      setNewUrl("");
      setNewTitle("");
      setNewInstructions("");
      setNewPreferred([]);
      setNotice({ key: "add", type: "success", text: "Source added." });
    } catch (e: any) {
      setNotice({ key: "add", type: "error", text: e?.message || "Failed to add." });
    } finally {
      setAdding(false);
    }
  };

  const sectionOptions = sections.map((s, i) => ({
    index: i,
    title: s.title || `Section ${i + 1}`,
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {/* Add source */}
      <div
        style={{
          border: "1px dashed #cbd5e0",
          borderRadius: "6px",
          padding: "12px",
          background: "#f7fafc",
        }}
      >
        <div style={{ fontWeight: 600, fontSize: "13px", color: "#2D3748", marginBottom: "8px" }}>
          Add source
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
          <input
            placeholder="URL (required)"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            style={inputStyle}
          />
          <input
            placeholder="Title"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            style={inputStyle}
          />
          <input
            placeholder="Instructions"
            value={newInstructions}
            onChange={(e) => setNewInstructions(e.target.value)}
            style={{ ...inputStyle, gridColumn: "1 / -1" }}
          />
          <label style={labelStyle}>
            Preferred sections (none = any)
            <select
              multiple
              size={3}
              value={newPreferred.map(String)}
              onChange={(e) =>
                setNewPreferred(
                  Array.from(e.target.selectedOptions).map((o) => Number(o.value)),
                )
              }
              style={inputStyle}
            >
              {sectionOptions.map((s) => (
                <option key={s.index} value={s.index}>
                  {s.title}
                </option>
              ))}
            </select>
          </label>
          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <button
              type="button"
              className="primary-button"
              onClick={handleAdd}
              disabled={adding || !newUrl.trim()}
            >
              {adding ? "Adding…" : "Add source"}
            </button>
          </div>
        </div>
      </div>

      {/* Source list */}
      {sources.length === 0 ? (
        <div style={{ color: "#718096", fontSize: "13px" }}>
          No sources for this organization yet.
        </div>
      ) : (
        sources.map((source) => (
          <div
            key={source.id}
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              padding: "12px",
            }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              <input
                defaultValue={source.url}
                disabled={pending.has(`${source.id}:url`)}
                onBlur={(e) =>
                  e.target.value !== source.url &&
                  run(`${source.id}:url`, () =>
                    onUpdate(source.id, { url: e.target.value }),
                  )
                }
                placeholder="URL"
                style={inputStyle}
              />
              <input
                defaultValue={source.title ?? ""}
                disabled={pending.has(`${source.id}:title`)}
                onBlur={(e) =>
                  e.target.value !== (source.title ?? "") &&
                  run(`${source.id}:title`, () =>
                    onUpdate(source.id, { title: e.target.value }),
                  )
                }
                placeholder="Title"
                style={inputStyle}
              />
              <input
                defaultValue={source.instructions ?? ""}
                disabled={pending.has(`${source.id}:instructions`)}
                onBlur={(e) =>
                  e.target.value !== (source.instructions ?? "") &&
                  run(`${source.id}:instructions`, () =>
                    onUpdate(source.id, { instructions: e.target.value }),
                  )
                }
                placeholder="Instructions"
                style={{ ...inputStyle, gridColumn: "1 / -1" }}
              />
              <label style={labelStyle}>
                Preferred sections (none = any)
                <select
                  multiple
                  size={3}
                  value={source.preferred_section_indexes.map(String)}
                  disabled={pending.has(`${source.id}:preferred`)}
                  onChange={(e) =>
                    run(`${source.id}:preferred`, () =>
                      onUpdate(source.id, {
                        preferred_section_indexes: Array.from(
                          e.target.selectedOptions,
                        ).map((o) => Number(o.value)),
                      }),
                    )
                  }
                  style={inputStyle}
                >
                  {sectionOptions.map((s) => (
                    <option key={s.index} value={s.index}>
                      {s.title}
                    </option>
                  ))}
                </select>
              </label>
              <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`Delete source ${source.url}?`)) {
                      run(`${source.id}:delete`, () => onDelete(source.id));
                    }
                  }}
                  disabled={pending.has(`${source.id}:delete`)}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid #e53e3e",
                    borderRadius: "4px",
                    background: "#fff",
                    color: "#c53030",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
            {notice && notice.key.startsWith(`${source.id}:`) && (
              <div
                style={{
                  marginTop: "6px",
                  fontSize: "12px",
                  color: notice.type === "success" ? "#2F855A" : "#C53030",
                }}
              >
                {notice.text}
              </div>
            )}
          </div>
        ))
      )}

      {notice && notice.key === "add" && (
        <div
          style={{
            fontSize: "12px",
            color: notice.type === "success" ? "#2F855A" : "#C53030",
          }}
        >
          {notice.text}
        </div>
      )}
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  fontSize: "13px",
  fontWeight: 600,
  color: "#4A5568",
};

const inputStyle: React.CSSProperties = {
  padding: "6px 10px",
  border: "1px solid #e2e8f0",
  borderRadius: "4px",
  fontSize: "13px",
  boxSizing: "border-box",
  width: "100%",
};

export default SourcesPanel;
